import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { revokeSession } from '@/shared/api/auth.api';
import { configureApiSession } from '@/shared/api/client';
import { AuthContext, type AuthContextValue } from '@/shared/auth/auth-context';
import type { Session } from '@/shared/auth/session';
import { clearStoredSession, readStoredSession, writeStoredSession } from '@/shared/auth/session.storage';

const EXPIRATION_MESSAGE = 'Sua sessão expirou. Entre novamente para continuar.';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Única fonte de verdade da sessão.
 *
 * Também é quem liga o cliente HTTP à sessão: o interceptor lê as credenciais
 * por referência (`sessionRef`), então trocar de sessão não exige recriar o
 * cliente axios nem re-registrar interceptors.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(() => readStoredSession());
  const [expirationNotice, setExpirationNotice] = useState<string | null>(null);

  /**
   * Espelho da sessão para o interceptor do axios, que lê fora do ciclo de
   * render. Mantido em sincronia pelos próprios setters (`signIn`, `signOut`,
   * `expire`) — nunca escrito durante o render.
   */
  const sessionRef = useRef<Session | null>(session);

  const expire = useCallback(() => {
    // Só avisa quem realmente estava logado — evita mostrar "sessão expirada"
    // para um 401 disparado por outro motivo na tela de login.
    if (sessionRef.current) {
      setExpirationNotice(EXPIRATION_MESSAGE);
    }
    sessionRef.current = null;
    clearStoredSession();
    setSession(null);
  }, []);

  useEffect(() => {
    configureApiSession({
      readCredentials: () => sessionRef.current,
      onUnauthorized: expire,
    });
  }, [expire]);

  const signIn = useCallback((newSession: Session) => {
    sessionRef.current = newSession;
    writeStoredSession(newSession);
    setExpirationNotice(null);
    setSession(newSession);
  }, []);

  const signOut = useCallback(() => {
    const current = sessionRef.current;
    sessionRef.current = null;
    clearStoredSession();
    setExpirationNotice(null);
    setSession(null);

    // Best-effort: a sessão local já foi descartada, uma falha aqui não muda a UI.
    // A credencial vai por argumento porque a rota exige sessão e o `sessionRef` já
    // é nulo quando o interceptor roda.
    if (current) {
      void revokeSession(current).catch(() => {});
    }
  }, []);

  const dismissExpirationNotice = useCallback(() => setExpirationNotice(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, expirationNotice, signIn, signOut, dismissExpirationNotice }),
    [session, expirationNotice, signIn, signOut, dismissExpirationNotice],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
